<script setup>
import { Pagination, A11y, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

const props = defineProps({
  slides: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: '',
  },
  isHome: {
    type: Boolean,
    default: false,
  },
})

const slider = ref(null)
const nextButton = ref(null)
const prevButton = ref(null)

const paddingLeft = ref(0)
const rowLimit = computed(() => (props.isHome ? 400 : 130))

const paddingLimit = computed(() => (props.isHome ? 1800 : 2180))

const isLargeScreen = ref(false)

const onResize = () => {
  if (typeof window !== 'undefined') {
    paddingLeft.value = Math.round((window.innerWidth - paddingLimit.value) / 2)
    isLargeScreen.value = window.innerWidth >= 1000
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

const onSwiper = (swiper) => {
  slider.value = swiper
}

const shouldShowButtons = computed(() => {
  if (!slider.value) return true
  const { isBeginning, isEnd } = slider.value
  return !(isBeginning && isEnd)
})
</script>

<template>
  <div
    class="main-swiper"
    :class="type === 'default-type' ? 'row-130-25 max' : ''"
  >
    <template v-if="type === 'default-type'">
      <!-- eslint-disable vue/no-v-html -->
      <div class="container" :class="[type]">
        <div class="container-sections">
          <swiper
            class="slider-swiper"
            :navigation="{
              nextEl: nextButton,
              prevEl: prevButton,
            }"
            :modules="[Pagination, A11y, Navigation]"
            :slides-per-view="1"
            :breakpoints="{
              //  >= 749px
              749: {
                slidesPerView: 2,
              },
              //  >= 1300px
              1300: {
                slidesPerView: 3,
              },
            }"
            :space-between="45"
            :pagination="{ clickable: false }"
            :lazy="true"
            @swiper="onSwiper"
          >
            <swiper-slide v-for="(slide, index) in slides" :key="index">
              <div class="slider-swiper__slide__wrap">
                <SwiperSlideContent :content="slide" :type="type" />
              </div>
            </swiper-slide>
            <!-- Navigation -->
            <div v-if="slides?.length > 1" class="swiper-buttons">
              <button
                ref="prevButton"
                class="swiper-button-prev"
                aria-label="Button Prev"
              ></button>
              <button
                ref="nextButton"
                class="swiper-button-next"
                aria-label="Button Next"
              ></button>
            </div>
          </swiper>
        </div>
      </div>
    </template>
    <template v-else-if="type === 'offset-type'">
      <!-- eslint-disable vue/no-v-html -->
      <div class="container" :class="[type, { 'is-home': isHome }]">
        <div class="container-sections">
          <swiper
            class="slider-swiper"
            :class="{
              '--calculated-padding': paddingLeft - rowLimit > 0,
            }"
            :style="{
              '--padding-left': `${paddingLeft}px`,
            }"
            :navigation="{
              nextEl: nextButton,
              prevEl: prevButton,
            }"
            :modules="[Pagination, A11y, Navigation]"
            :pagination="{ clickable: false }"
            :lazy="true"
            :space-between="15"
            :slides-per-view="1"
            :breakpoints="{
              //  >= 1000px
              1000: {
                slidesPerView: 'auto',
                spaceBetween: 0,
              },
            }"
            @swiper="onSwiper"
          >
            <swiper-slide v-for="(slide, index) in slides" :key="index">
              <div class="slider-swiper__slide__wrap">
                <SwiperSlideContent :content="slide" :type="type" />
              </div>
            </swiper-slide>
            <!-- Navigation -->
            <div v-if="!isLargeScreen" class="swiper-buttons hide-for-large">
              <div>
                <button
                  ref="prevButton"
                  class="swiper-button-prev"
                  aria-label="Button Prev"
                ></button>
                <button
                  ref="nextButton"
                  class="swiper-button-next"
                  aria-label="Button Next"
                ></button>
              </div>
            </div>
          </swiper>
          <!-- Navigation -->
          <div
            v-if="isLargeScreen && shouldShowButtons"
            class="swiper-buttons row-130-25 max show-for-large"
          >
            <div>
              <button
                ref="prevButton"
                class="swiper-button-prev"
                aria-label="Button Prev"
              ></button>
              <button
                ref="nextButton"
                class="swiper-button-next"
                aria-label="Button Next"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="type === 'single-slide-type'">
      <!-- eslint-disable vue/no-v-html -->
      <div class="container" :class="[type]">
        <div class="container-sections">
          <swiper
            class="slider-swiper"
            :navigation="{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }"
            :modules="[Pagination, A11y, Navigation]"
            :pagination="{ clickable: false }"
            :lazy="true"
            :space-between="15"
            :slides-per-view="1"
            @swiper="onSwiper"
          >
            <swiper-slide v-for="(slide, index) in slides" :key="index">
              <div class="slider-swiper__slide__wrap">
                <SwiperSlideContent :content="slide" :type="type" />
              </div>
            </swiper-slide>
            <!-- Navigation -->
            <div class="swiper-buttons">
              <button
                class="swiper-button-prev"
                aria-label="Button Prev"
              ></button>
              <button
                class="swiper-button-next"
                aria-label="Button Next"
              ></button>
            </div>
          </swiper>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'Slider',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
